import React, { FunctionComponent, ReactElement, memo } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import Caption from '../../Theme/Fonts/Caption'
import Badge from '../Badges'
import { TestID } from '../../utils/PropTypesDefinitions'
import Image from '../Image'

export type ChipProps = {
    /** test to show inside of the chip */
    text: string
    /**
     * callback function triggered when chip is pressed
     *
     * @TJS-type function
     */
    onPress?: () => void
    /** if count is provided, the number is shown on the right hand side of the chip */
    count?: number
    /** shows chip in active color */
    active?: boolean
    /** if the url exits the image gets displayed*/
    imageUrl?: string
    /** extra option for inactive cjip with border around, additionally set active to false to see the border*/
    withOutline?: boolean
    /** two different background colors for the inactive chip
     * @defaultValue 'pearl'
     * */
    inactiveBackgroundColor?: 'pearl' | 'tahitian-4' | 'BLACK'
} & TestID

const getBorderProps = (withOutline?: boolean, active?: boolean) => {
    if (withOutline && !active) {
        return { borderColor: '#979797', border: 1 }
    }
    return {}
}

export const getInactiveFontColorByBackgroundColor = (
    backgroundColor?: ChipProps['inactiveBackgroundColor']
): string => {
    if (backgroundColor === 'BLACK') {
        return 'WHITE'
    }
    return 'onyx'
}

const Chip: FunctionComponent<ChipProps> = ({
    text,
    onPress,
    count,
    active,
    testID,
    imageUrl,
    inactiveBackgroundColor = 'pearl',
    withOutline,
}): ReactElement => {
    const { border, borderColor } = getBorderProps(withOutline, active)
    return (
        <Grid noExpand horizontal testID={testID}>
            <Touchable onPress={onPress}>
                <Grid
                    borderRadius={8}
                    backgroundColor={active ? 'onyx' : inactiveBackgroundColor}
                    padding={3}
                    elevation={active ? 0 : 3}
                    noExpand
                    horizontal
                    centerVertical
                    height={32}
                    border={border}
                    borderColor={borderColor}
                >
                    {imageUrl ? (
                        <Grid
                            borderRadius={6}
                            backgroundColor={
                                inactiveBackgroundColor === 'tahitian-4' ? 'pearl' : 'moon-3'
                            }
                            width={22}
                            height={22}
                            center
                            noExpand
                            marginLeft="-3px"
                            marginRight={2}
                        >
                            <Image
                                aspectRatio="1:1"
                                width={14}
                                height={14}
                                size="L"
                                imageUrl={imageUrl}
                                resizeMode="contain"
                            />
                        </Grid>
                    ) : null}
                    <Grid noExpand marginRight={count ? 3 : 0}>
                        <Caption
                            color={
                                active
                                    ? 'pearl'
                                    : getInactiveFontColorByBackgroundColor(inactiveBackgroundColor)
                            }
                        >
                            {text}
                        </Caption>
                    </Grid>
                    {count && count > 0 ? (
                        <Badge backgroundColor={active ? 'pearl' : 'onyx'}>{count}</Badge>
                    ) : null}
                </Grid>
            </Touchable>
        </Grid>
    )
}

export default memo(Chip)
